import { Injectable, Signal } from '@angular/core';
import { FeatureGroup, Layer, LatLngBounds } from 'leaflet';
import { combineLoadingStates } from '../../../shared/util/combineLoadingStates';
import { DuctService } from './duct/duct.service';
import { LandAgreementService } from './land-agreement/land-agreement.service';
import { PermitAreaService } from './permit/permit-area.service';
import { PointService } from './point/point.service';
import { SpanService } from './span/span.service';

@Injectable({ providedIn: 'root' })
export class MapService {
  private currentProject: string;
  readonly version: Signal<string>;

  constructor(
    private spanService: SpanService,
    private landAgreementService: LandAgreementService,
    private pointService: PointService,
    private ductService: DuctService,
    private permitareaService: PermitAreaService,
  ) {
    this.version = this.spanService.version;
  }

  getLoadingState$() {
    return combineLoadingStates([
      ...this.getLayerServices().map((service) => service.getLoadingState$()),
    ]);
  }

  getLayers() {
    return [
      this.landAgreementService.getLayerData(),
      this.spanService.getLayerData(),
      ...Object.values(this.pointService.getLayerData()),
    ] as Layer[];
  }

  getMapBounds() {
    const spanLayerBounds = this.spanService.getLayerData()?.getBounds();
    const pointLayerBounds = Object.values(
      this.pointService.getLayerData(),
    )[0]?.getBounds?.();

    if (spanLayerBounds?.isValid()) {
      return spanLayerBounds;
    } else if (pointLayerBounds?.isValid()) {
      return pointLayerBounds;
    } else {
      return new LatLngBounds([
        [-90, -180],
        [90, 180],
      ]);
    }
  }

  setMapBounds() {
    const featureGroup = new FeatureGroup();
    featureGroup.addLayer(this.landAgreementService.getLayerData() as Layer);
    return featureGroup.getBounds();
  }

  initializeMap(ifs: string) {
    if (ifs !== this.currentProject) {
      this.currentProject = ifs;
      this.clearData();
      this.fetchData(ifs);
    }
  }

  getVersion() {
    return this.version();
  }

  private fetchData(ifs: string) {
    this.ductService.fetchEntity(ifs);
    this.getLayerServices().forEach((service) => service.loadData(ifs));
  }

  private clearData() {
    this.ductService.clearData();
    this.getLayerServices().forEach((service) => service.clearData());
  }

  private getLayerServices() {
    return [
      this.spanService,
      this.landAgreementService,
      this.pointService,
      this.permitareaService,
    ];
  }
}
